import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Group,
  Loader,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import { listItemUoms } from '../items/api';
import { scanLocation, type ScannedLocationDto } from '../inventory/api';
import {
  saveReceiptLine,
  type ReceiptLineDto,
  type ReceivableLineDto,
} from './api';

/** Legacy's Location column is varchar(15). */
const LOCATION_MAX = 15;

interface Props {
  receiptBatchId: string;
  warehouseId: string;
  /** The order line being received against. Carries the item and its lot/serial flags. */
  receivable: ReceivableLineDto | null;
  /** Null adds a line; a row edits it. */
  line: ReceiptLineDto | null;
  opened: boolean;
  onClose: () => void;
  onSaved: (lines: ReceiptLineDto[]) => void;
}

/**
 * One line of a delivery: how much of one ordered item turned up, and where it went.
 *
 * The item is not a field. A receipt line exists against a purchase order line, and the operator
 * got here by choosing which one — asking again would be asking the same question twice, with a
 * chance of the two answers differing.
 *
 * The location is a SCAN field rather than a picker, using the same resolver the adjustment screen
 * uses: one barcode is either a location or a license plate, and the person at the dock has the
 * label in front of them. An unrecognised code is offered as a new pallet, because a receipt is
 * exactly when a fresh pallet is built and the label in somebody's hand is not enrolled yet.
 *
 * Lot and serial appear only for items that need them, and a serialized line is one unit in the
 * stocking unit — locked here, and enforced by the server too (53823/53824).
 */
export function ReceiptLineModal({
  receiptBatchId,
  warehouseId,
  receivable,
  line,
  opened,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const isEdit = line !== null;

  const [quantity, setQuantity] = useState<number | ''>('');
  const [uomId, setUomId] = useState('');
  const [locationText, setLocationText] = useState('');
  const [resolvedLocation, setResolvedLocation] = useState<ScannedLocationDto | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [scanningLocation, setScanningLocation] = useState(false);
  const [lotNumber, setLotNumber] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  /** yyyy-mm-dd, straight from a native date input. Revealed by a lot_needs_expiration refusal. */
  const [lotExpiration, setLotExpiration] = useState('');
  const [needsExpiration, setNeedsExpiration] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ---- Starting a new license plate ---------------------------------------
  /** The unrecognised code, held while the operator is asked what it is. */
  const [pendingPlate, setPendingPlate] = useState<string | null>(null);
  /** They said yes: this is the plate being built, and it needs somewhere to stand. */
  const [newPlateNumber, setNewPlateNumber] = useState('');
  const [plateHomeText, setPlateHomeText] = useState('');
  const [plateHome, setPlateHome] = useState<ScannedLocationDto | null>(null);
  const [plateHomeError, setPlateHomeError] = useState<string | null>(null);
  const [scanningPlateHome, setScanningPlateHome] = useState(false);
  /** Asked once per line. A second prompt after a genuine typo is an interrogation. */
  const [platePromptDeclined, setPlatePromptDeclined] = useState(false);

  const locationRef = useRef<HTMLInputElement>(null);
  const plateHomeRef = useRef<HTMLInputElement>(null);

  const itemId = receivable?.itemId ?? line?.itemId ?? '';

  const itemUoms = useQuery({
    queryKey: ['item-uoms', itemId || 'none'] as const,
    queryFn: ({ signal }) => listItemUoms(itemId, signal),
    enabled: opened && Boolean(itemId),
  });

  const stockingUom = useMemo(
    () => (itemUoms.data ?? []).find((u) => u.isStockingUom) ?? null,
    [itemUoms.data],
  );

  const serialized = receivable?.isSerialized ?? false;
  const lotControlled = receivable?.isLotControlled ?? false;

  useEffect(() => {
    if (!opened) return;
    setErrors({});
    setSubmitError(null);
    setNeedsExpiration(false);
    setLotExpiration('');
    setPendingPlate(null);
    setNewPlateNumber('');
    setPlateHomeText('');
    setPlateHome(null);
    setPlateHomeError(null);
    setPlatePromptDeclined(false);
    setLocationError(null);

    if (line) {
      setQuantity(line.quantityReceived);
      setUomId(line.uomId);
      setLocationText(line.licensePlateNumber ?? line.locationCode);
      setLotNumber(line.lotNumber ?? '');
      setSerialNumber(line.serialNumber ?? '');
      // The saved line already names a real location, so it is treated as resolved rather than
      // re-scanned: reopening an edit should not make somebody prove the location again.
      setResolvedLocation({
        kind: line.licensePlateId ? 'license_plate' : 'location',
        locationId: line.locationId,
        locationCode: line.locationCode,
        locationName: null,
        isForwardPick: false,
        isActive: true,
        balanceTypeId: 0,
        balanceTypeCode: '',
        balanceTypeName: '',
        warehouseId,
        warehouseCode: '',
        licensePlateId: line.licensePlateId,
        licensePlateNumber: line.licensePlateNumber,
      });
      return;
    }

    // A new line defaults to what is still owed, because receiving the whole balance is the common
    // case and typing it again is the common waste. Serialized items are one per line regardless.
    setQuantity(receivable ? (receivable.isSerialized ? 1 : receivable.balanceDue) : '');
    setUomId(receivable?.uomId ?? '');
    setLocationText('');
    setResolvedLocation(null);
    setLotNumber('');
    setSerialNumber('');
  }, [opened, line, receivable, warehouseId]);

  // A serialized item can only be received in its stocking unit, so the ordered unit does not get
  // a vote. The server refuses anything else (53824).
  useEffect(() => {
    if (serialized && stockingUom) setUomId(stockingUom.uomId);
  }, [serialized, stockingUom]);

  /**
   * Resolves the location field. A failure clears it and takes focus back — a mis-scan costs one
   * beep instead of a refused save.
   */
  const runLocationScan = useCallback(async () => {
    const code = locationText.trim();
    if (!code) {
      setResolvedLocation(null);
      setLocationError(null);
      return;
    }
    if (resolvedLocation) {
      const already = resolvedLocation.licensePlateNumber ?? resolvedLocation.locationCode;
      if (already === code.toUpperCase()) return;
    }

    setScanningLocation(true);
    setLocationError(null);
    try {
      const found = await scanLocation(code, warehouseId || undefined);
      setResolvedLocation(found);
      setLocationText(found.licensePlateNumber ?? found.locationCode);
    } catch (err) {
      setResolvedLocation(null);

      // Not a location and not a plate. On a receipt that is usually a label for a pallet being
      // built right now, so ask rather than refuse.
      const unknown = err instanceof ApiError && err.problem.type?.includes('scan_unknown');
      if (unknown && !platePromptDeclined) {
        setPendingPlate(code.toUpperCase());
        setLocationError(null);
        return;
      }

      setLocationText('');
      setLocationError(
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title ?? t('receipts.locationUnknown'))
          : t('receipts.locationUnknown'),
      );
      locationRef.current?.focus();
    } finally {
      setScanningLocation(false);
    }
  }, [locationText, resolvedLocation, warehouseId, platePromptDeclined, t]);

  /** Where a new plate is going to stand. It has to be a real location: a pallet cannot sit on one. */
  const runPlateHomeScan = useCallback(async () => {
    const code = plateHomeText.trim();
    if (!code) {
      setPlateHome(null);
      setPlateHomeError(null);
      return;
    }
    if (plateHome && plateHome.locationCode === code.toUpperCase()) return;

    setScanningPlateHome(true);
    setPlateHomeError(null);
    try {
      const found = await scanLocation(code, warehouseId || undefined);
      if (found.kind === 'license_plate') {
        setPlateHome(null);
        setPlateHomeText('');
        setPlateHomeError(t('receipts.plateHomeNotAPlate'));
        plateHomeRef.current?.focus();
        return;
      }
      setPlateHome(found);
      setPlateHomeText(found.locationCode);
    } catch (err) {
      setPlateHome(null);
      setPlateHomeText('');
      setPlateHomeError(
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title ?? t('receipts.locationUnknown'))
          : t('receipts.locationUnknown'),
      );
      plateHomeRef.current?.focus();
    } finally {
      setScanningPlateHome(false);
    }
  }, [plateHomeText, plateHome, warehouseId, t]);

  function acceptNewPlate() {
    setNewPlateNumber(pendingPlate ?? '');
    setPendingPlate(null);
    setPlateHomeText('');
    setPlateHome(null);
    setPlateHomeError(null);
    window.setTimeout(() => plateHomeRef.current?.focus(), 0);
  }

  function declineNewPlate() {
    setPendingPlate(null);
    setPlatePromptDeclined(true);
    setLocationText('');
    setLocationError(null);
    locationRef.current?.focus();
  }

  function cancelNewPlate() {
    setNewPlateNumber('');
    setPlateHomeText('');
    setPlateHome(null);
    setPlateHomeError(null);
    setLocationText('');
    locationRef.current?.focus();
  }

  const mutation = useMutation({
    mutationFn: () =>
      saveReceiptLine(receiptBatchId, line?.receiptBatchLineId ?? null, {
        purchaseOrderLineId: receivable?.purchaseOrderLineId ?? line!.purchaseOrderLineId!,
        // Building a plate means the location is where the plate stands; the scanned text is the
        // plate's number, not a place.
        locationId: (newPlateNumber ? plateHome! : resolvedLocation!).locationId,
        quantityReceived: Number(quantity),
        uomId: uomId || null,
        licensePlateId: newPlateNumber ? null : resolvedLocation!.licensePlateId,
        newLicensePlateNumber: newPlateNumber || null,
        lotNumber: lotControlled ? lotNumber.trim().toUpperCase() : null,
        serialNumber: serialized ? serialNumber.trim().toUpperCase() : null,
        // Midnight UTC on the chosen day. A lot expires on a date, not an instant, so anchoring it
        // to the browser's zone would move the date for anybody east or west of the server.
        lotExpirationDateUtc: lotExpiration ? `${lotExpiration}T00:00:00Z` : null,
      }),
    onSuccess: (lines) => {
      onSaved(lines);
      onClose();
    },
    onError: (err) => {
      if (!(err instanceof ApiError)) {
        setSubmitError(t('receipts.errors.lineFailed'));
        return;
      }
      // Not a failure so much as a question: the lot is new and the item expires, so reveal the
      // date field and let them save again.
      if (err.problem.type?.includes('lot_needs_expiration')) {
        setNeedsExpiration(true);
        setSubmitError(err.problem.detail ?? t('receipts.lotNeedsExpiration'));
        return;
      }
      // Over-receipt spells out the arithmetic — ordered, arrived, pending elsewhere — and the
      // duplicate-serial refusal names the serial. Nothing restated here would be as useful.
      setSubmitError(err.problem.detail ?? err.problem.title ?? t('receipts.errors.lineFailed'));
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const next: Record<string, string> = {};
    if (newPlateNumber) {
      if (!plateHome) next.plateHome = t('receipts.plateHomeRequired');
    } else if (!resolvedLocation) {
      next.location = t('receipts.locationRequired');
    }
    if (quantity === '' || Number(quantity) <= 0) {
      next.quantity = t('receipts.errors.quantityPositive');
    }
    if (lotControlled && !lotNumber.trim()) next.lot = t('receipts.lotRequired');
    if (serialized && !serialNumber.trim()) next.serial = t('receipts.serialRequired');
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    mutation.mutate();
  }

  /** A barcode ends with Enter, so Enter resolves the field rather than submitting the form. */
  function scanKey(run: () => void) {
    return (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      run();
    };
  }

  const itemNumber = receivable?.itemNumber ?? line?.itemNumber ?? '';
  const itemDescription = receivable?.itemDescription ?? line?.itemDescription ?? '';

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={
        isEdit
          ? t('receipts.editLine', { number: line!.lineNumber })
          : t('receipts.receiveItem', { item: itemNumber })
      }
      width="md"
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          <div>
            <Text fw={500}>{itemNumber}</Text>
            <Text size="sm" c="dimmed">
              {itemDescription}
            </Text>
          </div>

          {/* The arithmetic behind the default quantity, spelled out. "Waiting at another door" is
              the term nobody expects, and it is the one that stops a delivery being booked twice. */}
          {receivable && !isEdit && (
            <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
              {t('receipts.balanceExplained', {
                ordered: receivable.quantityOrdered.toLocaleString(),
                arrived: receivable.quantityReceived.toLocaleString(),
                pending: receivable.pendingInOtherBatches.toLocaleString(),
                due: receivable.balanceDue.toLocaleString(),
                unit: receivable.uomCode,
              })}
            </Alert>
          )}

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            {/* Fixed at 1 and read-only for a serialized item — one serial number is one physical
                thing, so there is nothing to type. The server enforces it too (53823). */}
            <NumberInput
              label={t('receipts.quantityReceived')}
              description={
                serialized ? t('receipts.quantitySerialized') : t('receipts.quantityHint')
              }
              value={quantity}
              onChange={(v) => setQuantity(v === '' ? '' : Number(v))}
              error={errors.quantity}
              min={0}
              decimalScale={4}
              disabled={serialized}
              required
            />
            <Select
              label={t('receipts.unit')}
              description={serialized ? t('receipts.unitSerialized') : t('receipts.unitHint')}
              data={(itemUoms.data ?? []).map((u) => ({
                value: u.uomId,
                label: `${u.uomCode} — ${u.displayName}`,
              }))}
              value={uomId || null}
              onChange={(v) => setUomId(v ?? '')}
              disabled={itemUoms.isLoading || serialized}
              searchable
              allowDeselect={false}
            />
          </SimpleGrid>

          <TextInput
            ref={locationRef}
            label={t('receipts.location')}
            description={
              newPlateNumber ? t('receipts.locationIsNewPlate') : t('receipts.locationHint')
            }
            placeholder={t('receipts.locationPlaceholder')}
            value={newPlateNumber || locationText}
            onChange={(e) => setLocationText(e.currentTarget.value.toUpperCase())}
            onBlur={() => void runLocationScan()}
            onKeyDown={scanKey(() => void runLocationScan())}
            error={errors.location}
            maxLength={LOCATION_MAX}
            rightSection={scanningLocation ? <Loader size="xs" /> : null}
            disabled={Boolean(newPlateNumber)}
            required
          />

          {pendingPlate && (
            <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
              <Stack gap="xs">
                <Text size="sm">{t('receipts.newPlatePrompt', { code: pendingPlate })}</Text>
                <Group gap="sm">
                  <Button size="xs" color="invetrak" onClick={acceptNewPlate}>
                    {t('receipts.newPlateYes')}
                  </Button>
                  <Button size="xs" variant="default" onClick={declineNewPlate}>
                    {t('receipts.newPlateNo')}
                  </Button>
                </Group>
              </Stack>
            </Alert>
          )}

          {newPlateNumber && (
            <>
              <TextInput
                ref={plateHomeRef}
                label={t('receipts.plateHome')}
                description={t('receipts.plateHomeHint')}
                placeholder={t('receipts.locationPlaceholder')}
                value={plateHomeText}
                onChange={(e) => setPlateHomeText(e.currentTarget.value.toUpperCase())}
                onBlur={() => void runPlateHomeScan()}
                onKeyDown={scanKey(() => void runPlateHomeScan())}
                error={errors.plateHome}
                maxLength={LOCATION_MAX}
                rightSection={scanningPlateHome ? <Loader size="xs" /> : null}
                required
              />

              {plateHomeError && (
                <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />} py="xs">
                  {plateHomeError}
                </Alert>
              )}

              <Group>
                <Button size="xs" variant="subtle" onClick={cancelNewPlate}>
                  {t('receipts.newPlateCancel')}
                </Button>
              </Group>
            </>
          )}

          {locationError && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />} py="xs">
              {locationError}
            </Alert>
          )}

          {resolvedLocation && !newPlateNumber && resolvedLocation.kind === 'license_plate' && (
            <Text size="sm" c="dimmed">
              {t('receipts.resolvedPlate', {
                plate: resolvedLocation.licensePlateNumber,
                location: resolvedLocation.locationCode,
              })}
            </Text>
          )}

          {lotControlled && (
            <TextInput
              label={t('receipts.lotNumber')}
              description={t('receipts.lotHint')}
              value={lotNumber}
              onChange={(e) => setLotNumber(e.currentTarget.value.toUpperCase())}
              error={errors.lot}
              required
            />
          )}

          {/* A native date input rather than a picker: this appears rarely, and the platform's own
              control is the one the operator's device already knows. */}
          {needsExpiration && (
            <TextInput
              type="date"
              label={t('receipts.lotExpiration')}
              description={t('receipts.lotExpirationHint')}
              value={lotExpiration}
              onChange={(e) => setLotExpiration(e.currentTarget.value)}
              required
            />
          )}

          {serialized && (
            <TextInput
              label={t('receipts.serialNumber')}
              description={t('receipts.serialHint')}
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.currentTarget.value.toUpperCase())}
              error={errors.serial}
              required
            />
          )}

          {submitError && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              {submitError}
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              {t('shell.cancel')}
            </Button>
            <Button type="submit" color="invetrak" loading={mutation.isPending}>
              {t('shell.saveChanges')}
            </Button>
          </Group>
        </Stack>
      </form>
    </AppModal>
  );
}
